import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Download, ArrowLeft, BookOpen, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  executive_summary: "Executive Summary",
  business_model: "Business Model & Strategy",
  location: "Location Strategy",
  operations: "Operations & Staffing",
  equipment: "Equipment & Supplies",
  interior_design: "Interior Design & Layout",
  menu: "Menu Development",
  marketing: "Marketing & Branding",
  financial: "Financial Planning",
  permits: "Permits & Compliance",
  technology: "Technology & Systems",
  timeline: "Implementation Timeline",
  general: "General",
};

const CATEGORY_ICONS = {
  executive_summary: "01",
  business_model: "02",
  location: "03",
  operations: "04",
  equipment: "05",
  interior_design: "06",
  menu: "07",
  marketing: "08",
  financial: "09",
  permits: "10",
  technology: "11",
  timeline: "12",
  general: "--",
};

export default function PlaybookViewer() {
  const { playbookId } = useParams();
  const navigate = useNavigate();
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    if (playbookId) {
      const loadPlaybook = async () => {
        try {
          const res = await axios.get(`${API}/playbooks/${playbookId}`);
          setPlaybook(res.data);
        } catch (e) {
          toast.error("Failed to load playbook");
        } finally {
          setLoading(false);
        }
      };
      loadPlaybook();
    }
  }, [playbookId]);

  const downloadPdf = async () => {
    try {
      toast.info("Generating PDF...");
      const res = await axios.get(`${API}/playbooks/${playbookId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${playbook?.title?.replace(/\s+/g, "_") || "playbook"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (e) {
      toast.error("Failed to generate PDF");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-stone-500">Loading playbook...</p>
        </div>
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-500">Playbook not found</p>
          <Button variant="ghost" className="mt-4" onClick={() => navigate("/playbooks")}>
            Back to Playbooks
          </Button>
        </div>
      </div>
    );
  }

  const sections = [...(playbook.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="flex h-screen" data-testid="playbook-viewer">
      {/* Table of Contents */}
      <div className="w-72 border-r border-stone-200/60 bg-white flex-shrink-0 hidden lg:flex flex-col">
        <div className="p-6 border-b border-stone-200/60">
          <Button
            data-testid="back-to-playbooks-btn"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/playbooks")}
            className="text-stone-500 hover:text-stone-900 mb-3 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> All Playbooks
          </Button>
          <h2
            className="text-lg font-semibold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {playbook.title}
          </h2>
          <p className="text-xs text-stone-400 mt-1">
            {sections.length} sections · {playbook.status}
          </p>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-0.5">
            {sections.map((section, idx) => (
              <button
                key={idx}
                data-testid={`toc-section-${idx}`}
                onClick={() => setActiveSection(idx)}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200
                  ${activeSection === idx
                    ? "bg-stone-900 text-stone-50"
                    : "hover:bg-stone-100 text-stone-600"
                  }`}
              >
                <span className={`text-xs font-mono w-6 text-center flex-shrink-0
                  ${activeSection === idx ? "text-stone-400" : "text-stone-400"}`}>
                  {CATEGORY_ICONS[section.category] || `${String(idx + 1).padStart(2, "0")}`}
                </span>
                <span className="truncate flex-1">{section.title}</span>
                {activeSection === idx && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </ScrollArea>
        <div className="p-4 border-t border-stone-200/60">
          <Button
            data-testid="download-pdf-sidebar-btn"
            onClick={downloadPdf}
            className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all duration-200 font-medium"
          >
            <Download className="w-4 h-4 mr-2" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col bg-stone-50/50">
        {/* Header */}
        <div className="glass-header px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/playbooks")}
              className="lg:hidden text-stone-500"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                {sections[activeSection]?.title || playbook.title}
              </h2>
              <p className="text-xs text-stone-500">
                {CATEGORY_LABELS[sections[activeSection]?.category] || ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mobile section nav */}
            <div className="lg:hidden flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={activeSection === 0}
                onClick={() => setActiveSection((p) => p - 1)}
              >
                Prev
              </Button>
              <span className="text-xs text-stone-500 px-2">
                {activeSection + 1}/{sections.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={activeSection === sections.length - 1}
                onClick={() => setActiveSection((p) => p + 1)}
              >
                Next
              </Button>
            </div>
            <Button
              data-testid="download-pdf-btn"
              variant="outline"
              size="sm"
              onClick={downloadPdf}
              className="border-stone-200 hover:bg-stone-50"
            >
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
          </div>
        </div>

        {/* Content Area */}
        <ScrollArea className="flex-1">
          <div className="max-w-4xl mx-auto px-6 py-12 animate-fade-in">
            {sections[activeSection] ? (
              <div className="playbook-markdown text-stone-800">
                <ReactMarkdown>{sections[activeSection].content}</ReactMarkdown>
              </div>
            ) : (
              <div className="text-center py-20">
                <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No content available</p>
              </div>
            )}

            {/* Section Navigation */}
            <div className="mt-16 pt-8 border-t border-stone-200 flex items-center justify-between">
              <Button
                data-testid="prev-section-btn"
                variant="ghost"
                disabled={activeSection === 0}
                onClick={() => setActiveSection((p) => p - 1)}
                className="text-stone-500 hover:text-stone-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {activeSection > 0 ? sections[activeSection - 1]?.title : ""}
              </Button>
              <Button
                data-testid="next-section-btn"
                variant="ghost"
                disabled={activeSection >= sections.length - 1}
                onClick={() => setActiveSection((p) => p + 1)}
                className="text-stone-500 hover:text-stone-900"
              >
                {activeSection < sections.length - 1 ? sections[activeSection + 1]?.title : ""}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
